import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../utils/supabase";

export default function SetPasswordPage() {
  const navigate = useNavigate();
  const [ready, setReady] = useState(null);
  const [password, setPassword] = useState("");
  const [passwordAgain, setPasswordAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    let alive = true;
    const { data: subscription } = supabase.auth.onAuthStateChange((event) => {
      if (!alive) return;
      if (["SIGNED_IN", "PASSWORD_RECOVERY", "INITIAL_SESSION"].includes(event)) {
        supabase.auth.getSession().then(({ data }) => {
          if (alive && data?.session) setReady(true);
        });
      }
    });

    const timer = window.setTimeout(async () => {
      const { data } = await supabase.auth.getSession();
      if (alive) setReady((current) => current ?? Boolean(data?.session));
    }, 1200);

    return () => {
      alive = false;
      window.clearTimeout(timer);
      subscription?.subscription?.unsubscribe?.();
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage(null);
    if (password.length < 8) {
      setMessage({ type: "error", text: "La contraseña debe tener al menos 8 caracteres." });
      return;
    }
    if (password !== passwordAgain) {
      setMessage({ type: "error", text: "Las contraseñas no coinciden." });
      return;
    }

    try {
      setBusy(true);
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setMessage({ type: "ok", text: "Contraseña creada. Ingresando al panel técnico…" });
      window.setTimeout(() => navigate("/operations-overview", { replace: true }), 1000);
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.message || "No se pudo guardar la contraseña.",
      });
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-white/15 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-500 focus:border-sky-500 focus:outline-none";

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0b0f14] px-4 text-white">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-6 shadow-2xl">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-semibold">Activar cuenta de técnico</h1>
          <p className="mt-2 text-sm text-slate-400">
            Definí la contraseña que vas a utilizar para ingresar a Calins App.
          </p>
        </div>

        {ready === null ? (
          <div className="py-5 text-center text-sm text-slate-400">Verificando la invitación…</div>
        ) : ready === false ? (
          <div className="space-y-4 text-center">
            <p className="text-sm leading-6 text-slate-300">
              La invitación no es válida o ya venció. Pedile a un administrador que envíe una nueva.
            </p>
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="w-full rounded-lg border border-white/15 px-4 py-3 font-medium hover:bg-white/5"
            >
              Ir al ingreso
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {message && (
              <div className={`rounded-lg border px-3 py-2.5 text-sm ${
                message.type === "ok"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                  : "border-rose-500/30 bg-rose-500/10 text-rose-200"
              }`}>
                {message.text}
              </div>
            )}
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Contraseña nueva (mínimo 8 caracteres)"
              className={inputClass}
            />
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={passwordAgain}
              onChange={(event) => setPasswordAgain(event.target.value)}
              placeholder="Repetí la contraseña"
              className={inputClass}
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-lg bg-emerald-600 px-4 py-3 font-semibold hover:bg-emerald-500 disabled:opacity-60"
            >
              {busy ? "Guardando…" : "Crear contraseña e ingresar"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
